/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";

function Header({ showLogin, onLogin, onLogout, isAuthDisabled }) {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isSandbox = user?.current_plan === "sandbox";

  const canReviewIncidents =
    user?.permissions?.includes("can_update_incident") ||
    user?.permissions?.includes("can_review_incident") ||
    hasRole("admin") ||
    hasRole("reviewer");

  const canViewArtifacts =
    user?.permissions?.includes("can_update_incident") ||
    user?.permissions?.includes("can_review_incident") ||
    user?.permissions?.includes("can_view_decision_artifacts") ||
    hasRole("admin") ||
    hasRole("reviewer");

  const canAccessAdmin = hasRole("admin") && !user?.is_b2c_user;
  const isFluxonixOperator = user?.email?.toLowerCase().endsWith("@fluxonix.io");

  const navClass = (path) => {
    const active = location.pathname === path || location.pathname.startsWith(`${path}/`);

    return `min-h-[44px] shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
      active
        ? "bg-primary text-white"
        : "text-primary hover:bg-primary/10"
    }`;
  };

  return (
    <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 shadow-sm">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-16 flex-wrap items-center gap-x-3 gap-y-1 py-2 md:flex-nowrap md:justify-between">
          {/* Logo */}
          <Link
            to="/secure/assist"
            className="shrink-0 text-lg font-bold text-primary transition-colors hover:text-primary/80 sm:text-xl"
          >
            Fluxonix<span className="hidden sm:inline"> Ops</span>
          </Link>

          {/* Navigation */}
          <nav className="order-3 -mx-4 flex w-[calc(100%+2rem)] items-center gap-1 overflow-x-auto px-4 pb-1 pt-1 [scrollbar-width:thin] sm:-mx-6 sm:w-[calc(100%+3rem)] sm:px-6 md:order-2 md:mx-0 md:w-auto md:flex-1 md:justify-center md:px-0">
            {user && (
              <>
                <Link to="/secure/assist" className={navClass("/secure/assist")}>Ask Fluxonix</Link>
                <Link
                  to="/secure/my-incidents"
                  className={navClass("/secure/incidents")}
                >
                  Incidents
                </Link>

                <Link
                  to="/operational-memory"
                  className={navClass("/operational-memory")}
                >
                  Operational Memory
                </Link>

                {user?.tenant_slug === "fluxonix" && user?.role !== "lpo_submitter" && (
                  <Link to="/secure/founder-memory" className={navClass("/secure/founder-memory")}>
                    Founder Memory
                  </Link>
                )}

                {canReviewIncidents && (
                  <Link
                    to="/secure/reviews"
                    className={navClass("/secure/reviews")}
                  >
                    Reviews
                  </Link>
                )}

                {/* {canViewArtifacts && (
                  <Link
                    to="/admin/artifacts"
                    className={navClass("/admin/artifacts")}
                  >
                    Artifacts
                  </Link>
                )} */}

                {canAccessAdmin && (
                  <Link
                    to="/secure/operations-center"
                    className={navClass("/secure/operations-center")}
                  >
                    Mission Control
                  </Link>
                )}

                {hasRole("admin") && (
                  <Link
                    to="/admin/team"
                    className={navClass("/admin/team")}
                  >
                    Team
                  </Link>
                )}

                {isFluxonixOperator && hasRole("admin") && (
                  <Link
                    to="/admin/onboarding/invitations"
                    className={navClass("/admin/onboarding/invitations")}
                  >
                    Onboarding
                  </Link>
                )}
              </>
            )}
          </nav>

          {/* Right side actions */}
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-2 md:order-3 md:ml-0">
            {showLogin && (
              <button
                onClick={onLogin}
                className="min-h-[44px] shrink-0 px-3 py-2 text-sm font-medium text-primary border border-primary/20 rounded-lg hover:bg-primary/10 transition-colors sm:px-4"
              >
                Login
              </button>
            )}

            {onLogout && (
              <button
                onClick={onLogout}
                className="min-h-[44px] shrink-0 px-3 py-2 text-sm font-medium text-primary border border-primary/20 rounded-lg hover:bg-primary/10 transition-colors sm:px-4"
              >
                Logout
              </button>
            )}

            {user && user.tenant_slug && (
              <div className="relative group">
                <button
                  aria-label="Settings"
                  disabled={isSandbox}
                  onClick={() => {
                    if (!isSandbox) {
                      navigate("/admin/billing");
                    }
                  }}
                  title={isSandbox ? "Settings are managed by Fluxonix during exploratory access." : "Settings"}
                  className={`min-h-[44px] min-w-[44px] p-2 rounded-lg transition-colors ${
                    isSandbox
                      ? "opacity-40 cursor-not-allowed"
                      : "hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer"
                  }`}
                >
                  <span className="material-symbols-outlined text-slate-600 dark:text-slate-400">
                    settings
                  </span>
                </button>

                <div className="absolute right-0 top-full mt-1 px-2 py-1 bg-slate-800 dark:bg-slate-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                  {isSandbox
                    ? "Settings are managed by Fluxonix during exploratory access."
                    : "Settings"}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
